// All platform translations required by shared components/utilities.
export const _platformRequiredTranslations = [
  'platform:error.generic.message',
  'platform:error.generic.title',
  'platform:error.generic.description',
  'platform:error.requestCancelled',

  // Auth-related translations required by platform utilities (e.g., tel-link)
  'platform:error.auth.unauthorized',
  'platform:error.auth.forbidden',

  'platform:processing',

  'platform:phoneInput.inputPlaceholder',
  'platform:phoneInput.searchPlaceholder',
  'platform:phoneInput.noCountryFound',

  'platform:fileUpload.dropText',
  'platform:fileUpload.selectButton',
  'platform:fileUpload.maxSize',
  'platform:fileUpload.removeAll',
  'platform:fileUpload.invalidFileType',

  'platform:fileUpload.presets.image.dropText',
  'platform:fileUpload.presets.image.selectButton',
] as const

export type PlatformRequiredTranslationKey = (typeof _platformRequiredTranslations)[number]

// Minimal indirection layer to bind platform i18n hooks from app-specific packages
const i18nConfig = {
  useTranslation: () => ({
    t: (key: PlatformRequiredTranslationKey, _params?: Record<string, unknown>) => key,
  }),
  useTranslatedText: (text: string | undefined) => text,
}

// Abbreviated to avoid import pollution
export const useTObject = () => i18nConfig.useTranslation()
export const useTrText = (text: string | undefined): string | undefined =>
  i18nConfig.useTranslatedText(text)

export const configurePlatformI18n = (
  // biome-ignore lint/suspicious/noExplicitAny: Hook type from external lib
  feLocaleUseTranslation: any,
  // biome-ignore lint/suspicious/noExplicitAny: Hook type from external lib
  feLocaleUseTranslatedText: any,
) => {
  i18nConfig.useTranslation = () => {
    const { t } = feLocaleUseTranslation()
    return {
      t: (key: PlatformRequiredTranslationKey, params?: Record<string, unknown>) => t(key, params),
    }
  }
  i18nConfig.useTranslatedText = feLocaleUseTranslatedText
}

// Typed "args" builder for platform translations used in Domain errors
export function platformI18tArgs<K extends PlatformRequiredTranslationKey>(
  key: K,
  params?: Record<string, unknown>,
): unknown[] {
  return params ? [key, params] : [key]
}
